import {
  AppstoreOutlined,
  AuditOutlined,
  BankOutlined,
  BarChartOutlined,
  CalendarOutlined,
  CarryOutOutlined,
  ClusterOutlined,
  ContactsOutlined,
  CreditCardOutlined,
  DashboardOutlined,
  DollarOutlined,
  ExclamationCircleOutlined,
  FileDoneOutlined,
  FileExclamationOutlined,
  FileTextOutlined,
  FolderOutlined,
  GiftOutlined,
  IdcardOutlined,
  LogoutOutlined,
  QuestionCircleOutlined,
  SettingOutlined,
  SwapOutlined,
  TagsOutlined,
  TeamOutlined,
  UnorderedListOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import { useSession } from '@horof/shared';
import { Badge, Button, Modal, Result } from 'antd';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import {
  createBrowserRouter,
  Link,
  Navigate,
  Outlet,
  useLocation,
} from 'react-router';
import { RouterProvider } from 'react-router/dom';
import { useQuery } from 'urql';
import { AssociationScreen } from './AssociationScreen';
import { BankScreen, StatementScreen } from './BankScreens';
import { BillScreen, BillsScreen, PaymentsScreen } from './BillingScreens';
import { CloseScreen } from './CloseScreen';
import { CockpitScreen } from './CockpitScreen';
import { DocumentsScreen } from './DocumentsScreen';
import { financeContext, ReviewCountDocument } from './data';
import { FindBox } from './FindBox';
import {
  AccountsScreen,
  CategoriesScreen,
  CounterpartiesScreen,
  ProjectsScreen,
} from './ListsScreens';
import { MembersScreen } from './MembersScreen';
import { MetersScreen } from './MetersScreen';
import { MonthScreen } from './MonthScreen';
import { DebtsScreen, InKindScreen, TransfersScreen } from './MovementsScreens';
import { OperationsScreen } from './OperationsScreen';
import { PricesScreen } from './PricesScreen';
import { MemberPage, MeterPage } from './RecordPages';
import { ReportsScreen } from './ReportsScreen';
import { ReviewScreen } from './ReviewScreen';
import { SettingsScreen } from './SettingsScreen';

/** Saved views of the operations: one click to a list of work to do. */
const VIEWS: {
  search: string;
  label: string;
  icon: ReactNode;
  hint?: string;
}[] = [
  { search: '', label: 'كل العمليات', icon: <UnorderedListOutlined /> },
  {
    search: '?missing=category',
    label: 'بلا صنف',
    icon: <TagsOutlined />,
    hint: 'عمليات لم يُحدَّد صنفها',
  },
  {
    search: '?missing=counterparty',
    label: 'بلا جهة',
    icon: <TeamOutlined />,
    hint: 'عمليات لم تُحدَّد جهتها',
  },
  {
    search: '?missing=receipt',
    label: 'مصاريف بلا وصل',
    icon: <FileExclamationOutlined />,
    hint: 'مصاريف لم يُرفق وصلها',
  },
];

/** Other screens, by section of the side menu. */
/** First in the menu: where things stand. */
const TOP: {
  to: string;
  label: string;
  icon: ReactNode;
  review?: boolean;
}[] = [
  { to: '/', label: 'لوحة القيادة', icon: <DashboardOutlined /> },
  { to: '/month', label: 'ملخص الشهر', icon: <CalendarOutlined /> },
  {
    to: '/review',
    label: 'المراجعة',
    icon: <ExclamationCircleOutlined />,
    review: true,
  },
];

const SECTIONS: {
  title: string;
  /** Shown only to this role (the API refuses the others anyway). */
  role?: 'committee';
  pages: { to: string; label: string; icon: ReactNode }[];
}[] = [
  {
    title: 'الحركات',
    pages: [
      { to: '/transfers', label: 'التحويلات', icon: <SwapOutlined /> },
      { to: '/debts', label: 'الديون', icon: <CreditCardOutlined /> },
      { to: '/in-kind', label: 'مساهمات عينية', icon: <GiftOutlined /> },
      { to: '/bank', label: 'كشوف البنك', icon: <AuditOutlined /> },
    ],
  },
  {
    title: 'الفواتير',
    pages: [
      { to: '/bills', label: 'الفواتير', icon: <FileDoneOutlined /> },
      { to: '/payments', label: 'الأداءات', icon: <WalletOutlined /> },
    ],
  },
  {
    title: 'الماء',
    role: 'committee',
    pages: [
      { to: '/close', label: 'إغلاق الشهر', icon: <CarryOutOutlined /> },
      { to: '/members', label: 'الأعضاء', icon: <TeamOutlined /> },
      { to: '/meters', label: 'العدادات', icon: <ClusterOutlined /> },
      { to: '/reports', label: 'التقارير', icon: <BarChartOutlined /> },
      { to: '/prices', label: 'الأسعار', icon: <DollarOutlined /> },
      { to: '/settings', label: 'الإعدادات', icon: <SettingOutlined /> },
    ],
  },
  {
    title: 'الجمعية',
    pages: [
      { to: '/association', label: 'بطاقة الجمعية', icon: <IdcardOutlined /> },
      { to: '/documents', label: 'الوثائق', icon: <FileTextOutlined /> },
    ],
  },
  {
    title: 'القوائم',
    pages: [
      { to: '/projects', label: 'المشاريع', icon: <FolderOutlined /> },
      { to: '/accounts', label: 'الحسابات', icon: <BankOutlined /> },
      { to: '/categories', label: 'الأصناف', icon: <AppstoreOutlined /> },
      { to: '/counterparties', label: 'الجهات', icon: <ContactsOutlined /> },
    ],
  },
];

/** The views' own keys: any other parameter (a filter) is kept apart. */
const VIEW_KEYS = ['review', 'missing'];

function viewOf(search: string): string {
  const params = new URLSearchParams(search);
  const own = new URLSearchParams();
  for (const key of VIEW_KEYS) {
    for (const v of params.getAll(key)) own.append(key, v);
  }
  const s = own.toString();
  return s ? `?${s}` : '';
}

/** A view's link, keeping the chosen period (`from` / `to`). */
function withPeriod(viewSearch: string, current: string): string {
  const params = new URLSearchParams(viewSearch);
  const now = new URLSearchParams(current);
  for (const key of ['from', 'to']) {
    const v = now.get(key);
    if (v) params.set(key, v);
  }
  const s = params.toString();
  return s ? `?${s}` : '';
}

/** Below this width the desk is not usable: the phone apps are. */
const MIN_WIDTH = 1024;

function useWidth() {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return width;
}

export const SHORTCUTS: [string, string][] = [
  ['Ctrl + K', 'البحث في كل شيء، أو لصق معرّف'],
  ['↓ / ↑', 'العملية التالية / السابقة'],
  ['Enter', 'فتح العملية'],
  ['Esc', 'إغلاق اللوحة'],
  ['Ctrl + S', 'حفظ'],
  ['Ctrl + Enter', 'حفظ والانتقال إلى التالية (أو إضافة أخرى)'],
  ['N', 'عملية جديدة'],
  ['/', 'البحث'],
  ['Ctrl + V', 'لصق صورة الوصل'],
];

function Layout() {
  const session = useSession();
  const location = useLocation();
  const width = useWidth();
  const [help, setHelp] = useState(false);
  const [{ data }] = useQuery({
    query: ReviewCountDocument,
    context: useMemo(() => financeContext, []),
  });
  const reviewCount = data?.financeReviewQueue?.totalCount ?? 0;
  const current = viewOf(location.search);
  // On a small screen the menu shrinks to its icons. Records open in a
  // drawer over the list (doc 59), so an open one no longer needs room.
  const collapsed = width < 1200;

  if (width < MIN_WIDTH) {
    return (
      <Result
        status="info"
        title="شاشة صغيرة"
        subTitle="مكتب الجمعية مصمَّم للحاسوب. على الهاتف، استعمل تطبيق المالية."
      />
    );
  }

  return (
    <div className={`desk${collapsed ? ' is-collapsed' : ''}`}>
      <header className="desk-header">
        <strong className="desk-title">مكتب الجمعية</strong>
        <FindBox />
        <span className="desk-header-side">
          <Button
            type="text"
            icon={<QuestionCircleOutlined />}
            onClick={() => setHelp(true)}
          >
            اختصارات
          </Button>
          <span>{session.displayName}</span>
          <Button
            type="text"
            icon={<LogoutOutlined />}
            onClick={session.logout}
            aria-label="تسجيل الخروج"
          />
        </span>
      </header>
      <nav className="desk-menu">
        {TOP.map((page) => (
          <Link
            key={page.to}
            to={page.to}
            title={collapsed ? page.label : undefined}
            className={`desk-menu-item${location.pathname === page.to ? ' is-active' : ''}`}
          >
            {page.icon}
            <span className="desk-menu-label">{page.label}</span>
            {page.review && reviewCount > 0 && (
              <Badge count={reviewCount} color="#c25e00" />
            )}
          </Link>
        ))}
        <div className="desk-menu-title desk-menu-gap">العمليات</div>
        {VIEWS.map((v) => (
          <Link
            key={v.search}
            to={`/operations${withPeriod(v.search, location.search)}`}
            title={collapsed ? v.label : v.hint}
            className={`desk-menu-item${location.pathname === '/operations' && current === v.search ? ' is-active' : ''}`}
          >
            {v.icon}
            <span className="desk-menu-label">{v.label}</span>
          </Link>
        ))}
        {SECTIONS.filter(
          (section) => !section.role || session.roles.includes(section.role),
        ).map((section) => (
          <div key={section.title} className="desk-menu-section">
            <div className="desk-menu-title">{section.title}</div>
            {section.pages.map((page) => (
              <Link
                key={page.to}
                to={page.to}
                title={collapsed ? page.label : undefined}
                className={`desk-menu-item${location.pathname.startsWith(page.to) ? ' is-active' : ''}`}
              >
                {page.icon}
                <span className="desk-menu-label">{page.label}</span>
              </Link>
            ))}
          </div>
        ))}
      </nav>
      <main className="desk-main">
        <Outlet />
      </main>
      <Modal
        open={help}
        title="اختصارات لوحة المفاتيح"
        footer={null}
        onCancel={() => setHelp(false)}
      >
        <table className="shortcuts">
          <tbody>
            {SHORTCUTS.map(([keys, what]) => (
              <tr key={keys}>
                <td>
                  <kbd className="ltr-num">{keys}</kbd>
                </td>
                <td>{what}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Modal>
    </div>
  );
}

const router = createBrowserRouter(
  [
    {
      element: <Layout />,
      children: [
        { path: '/', element: <CockpitScreen /> },
        { path: '/month', element: <MonthScreen /> },
        { path: '/review', element: <ReviewScreen /> },
        { path: '/operations', element: <OperationsScreen /> },
        { path: '/transfers', element: <TransfersScreen /> },
        { path: '/debts', element: <DebtsScreen /> },
        { path: '/in-kind', element: <InKindScreen /> },
        { path: '/bank', element: <BankScreen /> },
        { path: '/bank/:id', element: <StatementScreen /> },
        { path: '/bills', element: <BillsScreen /> },
        { path: '/bills/:billId', element: <BillScreen /> },
        { path: '/payments', element: <PaymentsScreen /> },
        { path: '/close', element: <CloseScreen /> },
        { path: '/members', element: <MembersScreen /> },
        { path: '/members/:memberId', element: <MemberPage /> },
        { path: '/meters', element: <MetersScreen /> },
        { path: '/meters/:meterId', element: <MeterPage /> },
        { path: '/reports', element: <ReportsScreen /> },
        { path: '/prices', element: <PricesScreen /> },
        { path: '/settings', element: <SettingsScreen /> },
        { path: '/association', element: <AssociationScreen /> },
        { path: '/documents', element: <DocumentsScreen /> },
        { path: '/projects', element: <ProjectsScreen /> },
        { path: '/accounts', element: <AccountsScreen /> },
        { path: '/categories', element: <CategoriesScreen /> },
        { path: '/counterparties', element: <CounterpartiesScreen /> },
        { path: '*', element: <Navigate to="/" replace /> },
      ],
    },
  ],
  { basename: import.meta.env.BASE_URL },
);

export function App() {
  return <RouterProvider router={router} />;
}
